import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  ChevronDown,
  Eye,
  RefreshCw,
  Download,
  User,
  Mail,
  Calendar,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { useCandidates } from '../hooks/useCandidates';
import { clsx } from 'clsx';

type CandidateStatus = 'new' | 'culture_survey_sent' | 'culture_survey_complete' | 'interviewing' | 'offer_sent' | 'hired' | 'rejected' | 'all';

const statusOptions: { value: CandidateStatus; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'culture_survey_sent', label: 'Culture Survey Sent' },
  { value: 'culture_survey_complete', label: 'Survey Complete' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offer_sent', label: 'Offer Sent' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

const roleOptions = [
  { value: 'all', label: 'All Roles' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'production', label: 'Production' },
  { value: 'driver', label: 'Driver' },
  { value: 'admin', label: 'Admin' },
  { value: 'sales', label: 'Sales' },
];

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  culture_survey_sent: 'bg-yellow-100 text-yellow-700',
  culture_survey_complete: 'bg-purple-100 text-purple-700',
  interviewing: 'bg-indigo-100 text-indigo-700',
  offer_sent: 'bg-perfectted-light text-perfectted-dark',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const surveyStatusColors: Record<string, string> = {
  not_sent: 'bg-gray-100 text-gray-600',
  sent: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
};

export default function HiringPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<CandidateStatus>(
    (searchParams.get('status') as CandidateStatus) || 'all'
  );
  const [selectedRole, setSelectedRole] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const { data: candidates, isLoading, refetch, isRefetching } = useCandidates();

  // Filter candidates
  const filteredCandidates = useMemo(() => {
    if (!candidates) return [];

    return candidates.filter((candidate) => {
      // Status filter
      if (selectedStatus !== 'all' && candidate.status !== selectedStatus) {
        return false;
      }

      // Role filter
      if (selectedRole !== 'all' && candidate.role !== selectedRole) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = candidate.name.toLowerCase().includes(query);
        const matchesEmail = candidate.email.toLowerCase().includes(query);
        const matchesRole = candidate.role.toLowerCase().includes(query);
        if (!matchesName && !matchesEmail && !matchesRole) {
          return false;
        }
      }

      return true;
    });
  }, [candidates, selectedStatus, selectedRole, searchQuery]);

  const clearFilters = () => {
    setSelectedStatus('all');
    setSelectedRole('all');
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedStatus !== 'all' || selectedRole !== 'all' || searchQuery;

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Hiring</h2>
          <p className="text-gray-500 mt-1">Manage candidates and applications</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4', isRefetching && 'animate-spin')} />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as CandidateStatus)}
              className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent bg-white"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Role filter */}
          <div className="relative">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent bg-white"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {filteredCandidates.length} of {candidates?.length ?? 0} candidates
      </div>

      {/* Candidates table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Candidate</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Role</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Applied Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Survey Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Source</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <RefreshCw className="w-5 h-5 animate-spin text-perfectted-primary" />
                      <span className="text-gray-500">Loading candidates...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredCandidates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No candidates found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredCandidates.map((candidate) => (
                  <tr key={candidate.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-perfectted-light flex items-center justify-center">
                          <User className="w-5 h-5 text-perfectted-dark" />
                        </div>
                        <div>
                          <Link
                            to={`/hiring/${candidate.id}`}
                            className="font-medium text-gray-900 hover:text-perfectted-primary"
                          >
                            {candidate.name}
                          </Link>
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-3.5 h-3.5" />
                            {candidate.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900 capitalize">{candidate.role}</td>
                    <td className="px-6 py-4">
                      <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', statusColors[candidate.status])}>
                        {formatStatus(candidate.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {format(new Date(candidate.appliedDate), 'dd MMM yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', surveyStatusColors[candidate.surveyStatus])}>
                        {formatStatus(candidate.surveyStatus)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{candidate.source || 'Direct'}</td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/hiring/${candidate.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-perfectted-primary hover:bg-perfectted-light rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
